'use client';

import { Button } from '@savvy-kit/components/button';
import { Calendar } from '@savvy-kit/components/calendar';
import { Label } from '@savvy-kit/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { format, formatISO } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import type { DateFilterMeta, DateOperator } from '../../types/filter-meta';
import type { DateFilterValue } from '../../types/filter-values';
import { useOperatorOptions } from './hooks/use-operator-options';
import { FilterActions } from './shared/filter-actions';
import { FilterOperatorSelect } from './shared/filter-operator-select';

export type DateFilterProps = {
  readonly filterMeta?: DateFilterMeta;
  readonly value: DateFilterValue | undefined;
  readonly onChange: (value: DateFilterValue | undefined) => void;
  readonly className?: string;
};

export function DateFilter({ filterMeta, value, onChange, className }: DateFilterProps) {
  const operators = useOperatorOptions('date', filterMeta);
  const operator: DateOperator = value?.operator ?? 'between';

  const singleDate =
    value && value.operator !== 'isEmpty' && value.operator !== 'between' ? new Date(value.value) : undefined;
  const startDate = value && value.operator === 'between' ? new Date(value.start) : undefined;
  const endDate = value && value.operator === 'between' ? new Date(value.end) : undefined;

  return (
    <div className={cn('flex flex-col gap-3 p-3', className)}>
      <FilterOperatorSelect
        id="date-filter-operator"
        value={operator}
        options={operators}
        onChange={(op) => (op === 'isEmpty' ? onChange({ operator: 'isEmpty' }) : onChange(undefined))}
      />

      {operator !== 'isEmpty' && operator !== 'between' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="date-filter-date">Date</Label>
          <Popover>
            <PopoverTrigger
              render={
                <Button
                  id="date-filter-date"
                  variant="outline"
                  size="sm"
                  className={cn('w-full justify-start text-left font-normal', !singleDate && 'text-muted-foreground')}
                />
              }
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {singleDate ? format(singleDate, 'PPP') : 'Pick a date'}
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={singleDate}
                onSelect={(d) => d && onChange({ operator, value: formatISO(d, { representation: 'date' }) })}
                autoFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {operator === 'between' && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="date-filter-start">From</Label>
            <Popover>
              <PopoverTrigger
                render={
                  <Button
                    id="date-filter-start"
                    variant="outline"
                    size="sm"
                    className={cn('w-full justify-start text-left font-normal', !startDate && 'text-muted-foreground')}
                  />
                }
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, 'PPP') : 'Start date'}
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(d) =>
                    d &&
                    onChange({
                      operator: 'between',
                      start: formatISO(d, { representation: 'date' }),
                      end: endDate
                        ? formatISO(endDate, { representation: 'date' })
                        : formatISO(d, { representation: 'date' }),
                    })
                  }
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="date-filter-end">To</Label>
            <Popover>
              <PopoverTrigger
                render={
                  <Button
                    id="date-filter-end"
                    variant="outline"
                    size="sm"
                    className={cn('w-full justify-start text-left font-normal', !endDate && 'text-muted-foreground')}
                  />
                }
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, 'PPP') : 'End date'}
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(d) =>
                    d &&
                    onChange({
                      operator: 'between',
                      start: startDate
                        ? formatISO(startDate, { representation: 'date' })
                        : formatISO(d, { representation: 'date' }),
                      end: formatISO(d, { representation: 'date' }),
                    })
                  }
                  autoFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      {value && <FilterActions onClear={() => onChange(undefined)} />}
    </div>
  );
}
